import { Badge, Tab, Tabs, Tooltip } from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { find, first, isEmpty, last, map, some } from "lodash";
import moment, { Moment } from "moment-timezone";
import { useEffect, useState } from "react";
import { useUsuario } from "../../../providers/usuarioProvider";
import Mes from "./mes";
import NenhumMes from "./nenhumMes";
import CadastrarPontoDePartida from "../arquivo/cadastrarPontoDePartida";
import Imposto from "../../../models/imposto";
import { NotaBovespa, NotaBMF } from "../../../models/nota";
import Usuario from "../../../models/usuario";
import { limit, onSnapshot, orderBy, query } from "firebase/firestore";
import ImpostoDb from "../../../db/impostoDb";
import UsuarioDb from "../../../db/usuarioDb";
import NotaDb from "../../../db/notaDb";
import { useLocation, useNavigate } from "react-router-dom";

export default function Timeline() {
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [impostos, setImpostos] = useState<Imposto[]>([]);
  const [primeiraNota, setPrimeiraNota] = useState<NotaBovespa | NotaBMF>();
  const [timeline, setTimeline] = useState<Moment[]>([]);
  const [usuario, setUsuario] = useState<Usuario>();
  const location = useLocation();
  const [mesSelecionado, setMesSelecionado] = useState("");

  useEffect(() => {
    if (visualizado) {
      return onSnapshot(
        new UsuarioDb().doc({ usuarioId: visualizado }),
        (snapshot) => {
          setUsuario(snapshot.data());
        }
      );
    }
  }, [visualizado]);

  const handleTabChange = (_: unknown, newValue: string) => {
    setMesSelecionado(newValue);
    navigate(`${location.pathname}#${newValue}`, { replace: true });
  };

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new ImpostoDb().collection({ usuarioId: visualizado, impostoId: "" }),
          orderBy("dataBase", "asc")
        ),
        (query) => {
          if (!query.empty) setImpostos(map(query.docs, (doc) => doc.data()));
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado) {
      return onSnapshot(
        query(
          new NotaDb().collection({ usuarioId: visualizado, notaId: "" }),
          orderBy("dataPregao", "asc"),
          limit(1)
        ),
        (query) => {
          if (!query.empty) setPrimeiraNota(query.docs[0].data());
          else setPrimeiraNota(undefined);
        }
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (!primeiraNota && !some(impostos)) {
      setTimeline([]);
    } else {
      let dataIterada: Moment = first(impostos)
        ? !!first(impostos)!.pontoDePartida
          ? first(impostos)!.dataBase.clone()
          : first(impostos)!.dataBase.clone().subtract(1, "month")
        : primeiraNota!.dataPregao.clone().subtract(1, "month");

      const ontem = moment.tz("America/Sao_Paulo").subtract(1, "day");
      let meses: Moment[] = [];
      while (dataIterada.isBefore(ontem)) {
        if (dataIterada.isSame(dataIterada.clone().endOf("month"), "day")) {
          meses.push(dataIterada.clone());
        }
        dataIterada.add(1, "day");
      }
      meses.push(ontem);
      setTimeline(meses);
      if (!!location.hash) {
        setMesSelecionado(location.hash.slice(1));
      } else {
        setMesSelecionado(last(meses)?.format("YYYY-MM") ?? "");
      }
    }
  }, [impostos, primeiraNota]);

  if (isEmpty(timeline)) return <NenhumMes />;
  else
    return (
      <TabContext value={mesSelecionado}>
        <Tabs
          sx={(theme) => ({
            bgcolor: theme.palette.background.default,
            // A régua sob as abas é a hairline do sistema, não uma sombra.
            borderBottom: `1px solid ${theme.palette.divider}`,
            // Aba de mês é NAVEGAÇÃO, não dado em coluna — então Mabry, não
            // mono. Com `labelMono` (uppercase + tracking +0.12em) as 12 abas
            // ficavam largas e gritadas, empurrando a régua para o scroll.
            "& .MuiTab-root": {
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: 0,
              textTransform: "capitalize",
              color: theme.palette.text.secondary,
            },
            "& .Mui-selected": {
              bgcolor: theme.palette.statusChip.accent.bg,
              // Tinta do accent (troca por tema) — `primary.main` reprova no dark.
              color: theme.palette.accentInk,
            },
            "& .MuiTabs-indicator": {
              bgcolor: theme.palette.accentInk,
            },
            "& .MuiTabScrollButton-root": {
              color: theme.palette.text.secondary,
            },
          })}
          value={mesSelecionado}
          onChange={handleTabChange}
          variant="scrollable"
          // color="primary"
          scrollButtons
          allowScrollButtonsMobile
        >
          {map(timeline, (mes) => {
            const impostoDoMes = find(impostos, (imposto) =>
              imposto.dataBase.isSame(mes, "month")
            );
            const recalcular = !!usuario?.atualizadoAte.isBefore(mes, "day");
            const mesAtual = mes.isSame(
              moment.tz("America/Sao_Paulo"),
              "month"
            );

            let tooltip: string = "";
            let cor: "default" | "warning" | "info" | "success" = "default";

            if (!impostoDoMes) {
              tooltip = "Esse mês ainda não foi calculado.";
            } else if (recalcular) {
              tooltip =
                "Alguma coisa pode ter mudado. Recalcule esse mês para garantir que está atualizado.";
              cor = "warning";
            } else if (mesAtual) {
              tooltip =
                "Os cálculos estão atualizados, mas como o mês não acabou você está vendo a parcial do mês.";
              cor = "info";
            } else {
              tooltip = "Esse mês já foi calculado e está atualizado!";
              cor = "success";
            }

            return (
              <Tab
                key={mes.format("YYYY-MM")}
                sx={{
                  padding: 1,
                  minWidth: 120,
                  opacity: 1,
                  position: "relative",
                }}
                label={
                  <Tooltip title={tooltip}>
                    <Badge
                      sx={{ "& .MuiBadge-badge": { top: -4 } }}
                      color={cor}
                      variant="dot"
                      invisible={!impostoDoMes}
                    >
                      {mes.format("MMMM/YY")}
                    </Badge>
                  </Tooltip>
                }
                value={mes.format("YYYY-MM")}
              />
            );
          })}
        </Tabs>
        {map(timeline, (mes, index) => {
          const impostoDoMes = find(impostos, (imposto) =>
            imposto.dataBase.isSame(mes, "month")
          );

          return (
            <TabPanel key={mes.format("YYYY-MM")} value={mes.format("YYYY-MM")}>
              {index === 0 ? (
                <CadastrarPontoDePartida dataBase={mes} />
              ) : (
                <Mes mes={mes} imposto={impostoDoMes} />
              )}
            </TabPanel>
          );
        })}
      </TabContext>
    );
}
